import "src/global.css";
// ----------------------------------------------------------------------
import * as Sentry from "@sentry/react";

import "./locales/i18n";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useEffect } from "react";
import "react-day-picker/style.css";
import ReactGA from "react-ga4";
import Joyride, { type CallBackProps, EVENTS } from "react-joyride";
import { useLocation, useNavigate } from "react-router";
import { AuthProvider } from "src/auth/context/auth";
import { useScrollToTop } from "src/hooks/use-scroll-to-top";
import Router from "src/pages";
import ErrorBoundary from "./components/error-boundary";
import NoInternetConnection from "./components/no-internet";
import Spinner from "./components/spinner";
import { SENTRY_KEY } from "./config-global";
import { AppProvider, useAppContext } from "./context/app-context";
import useNetworkStatus from "./hooks/use-network-status";
import ReloadPrompt from "./ReloadPrompt";

// import { trackPageView } from "./lib/ga";

// ----------------------------------------------------------------------
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: -1,
			refetchOnWindowFocus: false,
		},
	},
});
Sentry.init({
	dsn: SENTRY_KEY,
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration({
			maskAllText: false,
			blockAllMedia: false,
		}),
	],
	enabled: process.env.NODE_ENV === "production",
	// Performance Monitoring
	tracesSampleRate: 1.0, //  Capture 100% of the transactions
	// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
	// tracePropagationTargets: ['localhost', /^https:\/\/api-develop.aliebrahimi\.me\/api/],
	// Session Replay
	replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});
function AppContent() {
	// Create a client
	useScrollToTop();
	const {
		setState,
		state: { run, stepIndex, steps },
	} = useAppContext();
	const navigate = useNavigate();

	// Steps are now defined in the context when id is set

	const handleCallback = (data: CallBackProps) => {
		const { action, index, type } = data;
		console.log({ action }, { index }, { type });

		const isPreviousAction = action === "prev";
		if (action === "close") {
			localStorage.setItem("isShownTour", "true");

			setState({ run: false, tourActive: false });
		} else if (type === EVENTS.STEP_AFTER) {
			console.log("0");
			if (!isPreviousAction && (index === 1 || index === 3)) {
				console.log("0.1");

				setState({ run: true, stepIndex: index + 1 });
			} else {
				if (index < 4) {
					console.log("0.2");

					if (isPreviousAction && !data?.step?.data?.previous) {
						console.log("3", data?.step?.data);

						setState({ stepIndex: index - 1 });
					} else {
						console.log("4");
						navigate(
							isPreviousAction && data?.step?.data?.previous
								? data?.step?.data?.previous
								: data?.step?.data?.next,
						);
						setState({ run: false });
					}
				}

				if (index === 4) {
					localStorage.setItem("isShownTour", "true");

					if (isPreviousAction && data?.step?.data?.previous) {
						setState({ run: false });
						navigate(data?.step?.data?.previous);
					} else {
						setState({ run: false, tourActive: false });
						navigate(data?.step?.data?.next);
					}
				}
			}
		}
	};
	const { status } = useNetworkStatus();
	// const [showErrorComponent, setShowErrorComponent] = useState(true);
	const location = useLocation();

	useEffect(() => {
		if (import.meta.env.NODE_ENV === "production") {
			const path = location.pathname + location.search;
			ReactGA.send({ hitType: "pageview", page: path, title: path });
		}
	}, [location.pathname, location.search]);
	// // const location = useLocation()
	return (
		<QueryClientProvider client={queryClient}>
			{/* <TransitionGroup> */}
			{/* <CSSTransition key={location.key} timeout={300} classNames="fade"> */}
			<ErrorBoundary>
				{/* {JSON.stringify(showErrorComponent)}- isOnline= {JSON.stringify(isOnline)} */}
				{status === "loading" ? (
					<Spinner />
				) : status === "offline" ? (
					// && showErrorComponent
					<NoInternetConnection
					//  setShowErrorComponent={setShowErrorComponent}
					/>
				) : (
					<AuthProvider>
						<Router />
						<Joyride
							callback={handleCallback}
							continuous
							showProgress
							run={run}
							stepIndex={stepIndex}
							steps={steps}
							styles={{
								options: {
									primaryColor: "oklch(58.84% 0.2244 262.18)",
								},
							}}
						/>
					</AuthProvider>
				)}
			</ErrorBoundary>
			{/* </CSSTransition> */}
			{/* </TransitionGroup> */}
			<ReloadPrompt />
			<ReactQueryDevtools initialIsOpen={false} />
		</QueryClientProvider>
	);
}

export default function App() {
	return (
		<AppProvider>
			<AppContent />
		</AppProvider>
	);
}
